import React, { useState, useEffect } from 'react';
import {
    Paper, Typography, Chip, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Grid, TextField, MenuItem, Select, FormControl, 
    InputLabel, TablePagination, Button, makeStyles
} from '@material-ui/core';
import LoaderWithBackDrop from "../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { getDQInsiderProfilesListService, getDQInsiderProfileByIdService } from "../../../../services/dq_insider.service";
import PBDQDetailsDialog from './DQInsiderDetailsDialog.comp';
import DQInsiderLedgerDialog from './DQInsiderLedgerDialog.comp';
import DQInsiderDqtLedgerDialog from './DQInsiderDqtLedgerDialog.comp';
import CreateInsiderButton from './CreateInsiderButton.comp';
import { 
    DQ_PRO_PLAN_KEY_VERBOSE, 
    STRIPE_COLLECTION_METHOD, 
    COLLECTION_METHOD_LABELS,
    STRIPE_SUBSCRIPTION_STATUS,
    STRIPE_SUBSCRIPTION_STATUS_VERBOSE,
    INSIDER_PROFILE_TYPE_VERBOSE
} from '../../../../utils/constants';
import Box from "@material-ui/core/Box";

const getProfileTypeLabel = (profile) => {
    const isEmailValid = profile.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email);
    if (!isEmailValid) return INSIDER_PROFILE_TYPE_VERBOSE.GUEST;
    
    const planKey = profile.dq_pro_plan?.plan_key;
    if (planKey === 'dq_pro') {
        return INSIDER_PROFILE_TYPE_VERBOSE.BUSINESS;
    }
    if (planKey === 'educator_plan') {
        return INSIDER_PROFILE_TYPE_VERBOSE.EDUCATOR;
    }
    
    if (profile.profile_type === 'INDIVIDUAL') return INSIDER_PROFILE_TYPE_VERBOSE.EDUCATOR;
    return INSIDER_PROFILE_TYPE_VERBOSE[profile.profile_type] || profile.profile_type || 'Individual';
};





const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    table: {
        minWidth: 650,
    },
    filterContainer: {
        marginBottom: theme.spacing(3),
    },
    tableHeaderCell: {
        verticalAlign: 'top',
    },
    actionButton: {
        lineHeight: 1.3,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        fontSize: '0.8rem',
        padding: '7px 10px',
    }
}));


const PBDQProfilesTab = () => {
    const classes = useStyles();

    // Data state
    const [loading, setLoading] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        subscription_status: '',
        collection_method: '',
        search: '',
        page: 1,
        limit: 50
    });

    // Dialog state
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Ledger Dialog state
    const [ledgerOpen, setLedgerOpen] = useState(false);
    const [selectedLedgerInsiderId, setSelectedLedgerInsiderId] = useState(null);

    // DQ Token ledger dialog — the DQT counterpart of the credit ledger above.
    const [dqtLedgerOpen, setDqtLedgerOpen] = useState(false);
    const [selectedDqtInsiderId, setSelectedDqtInsiderId] = useState(null);

    useEffect(() => {
        fetchData();
    }, [filters.page, filters.limit, filters.subscription_status, filters.collection_method]);

    // Separate effect for search debounce
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (filters.page !== 1) {
                setFilters(prev => ({ ...prev, page: 1 }));
            } else {
                fetchData();
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [filters.search]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { subscription_status, collection_method, search, page, limit } = filters;
            const result = await getDQInsiderProfilesListService(
                subscription_status || undefined,
                collection_method || undefined,
                search || undefined,
                page,
                limit
            );
            
            if (result && result.data && result.data.profiles) {
                console.log('Profiles List Result:', result);
                setProfiles(result.data.profiles);
                setTotal(result.data.total || 0);
            }




        } catch (error) {
            console.error('Error fetching profiles:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1
        }));
    };

    const handleViewDetails = async (dq_insider_id) => {
        setDetailsOpen(true);
        setDetailsLoading(true);
        setSelectedProfile(null);
        try {
            const result = await getDQInsiderProfileByIdService(dq_insider_id);
            if (result && result.data) {
                setSelectedProfile(result.data);
            }
        } catch (error) {
            console.error('Error fetching profile details:', error);
        } finally {
            setDetailsLoading(false);
        }
    };

    const refreshSelectedProfile = async () => {
        const id = selectedProfile?.dq_insider_id;
        if (!id) return;
        try {
            const result = await getDQInsiderProfileByIdService(id);
            if (result && result.data) {
                setSelectedProfile(result.data);
            }
        } catch (error) {
            console.error('Error refreshing profile details:', error);
        }
        fetchData();
    };

    const handleDetailsClose = () => {
        setDetailsOpen(false);
        setSelectedProfile(null);
        fetchData();
    };

    const handleViewDqtLedger = (dq_insider_id) => {
        setSelectedDqtInsiderId(dq_insider_id);
        setDqtLedgerOpen(true);
    };

    const handleViewLedger = (dq_insider_id) => {
        setSelectedLedgerInsiderId(dq_insider_id);
        setLedgerOpen(true);
    };

    return (
        <div>
            <Paper className={classes.paper} elevation={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 20 }}>
                    <Typography variant="h6" gutterBottom style={{ fontWeight: 600, margin: 0 }}>
                        PBDQ Profiles List
                    </Typography>
                    <CreateInsiderButton onCreated={fetchData} />
                </Box>

                <Grid container spacing={2} className={classes.filterContainer}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Search Email / Name"
                            autoComplete="off"
                            inputProps={{ autoComplete: 'off' }}
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Subscription Status</InputLabel>
                            <Select
                                value={filters.subscription_status}
                                onChange={(e) => handleFilterChange('subscription_status', e.target.value)}
                                label="Subscription Status"
                            >
                                <MenuItem value=""><em>All</em></MenuItem>
                                {Object.values(STRIPE_SUBSCRIPTION_STATUS).map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {STRIPE_SUBSCRIPTION_STATUS_VERBOSE[status] || status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Payment Collection Method</InputLabel>
                            <Select
                                value={filters.collection_method}
                                onChange={(e) => handleFilterChange('collection_method', e.target.value)}
                                label="Payment Collection Method"
                            >

                                <MenuItem value=""><em>All</em></MenuItem>
                                <MenuItem value={STRIPE_COLLECTION_METHOD.CHARGE_AUTOMATICALLY}>
                                    {COLLECTION_METHOD_LABELS[STRIPE_COLLECTION_METHOD.CHARGE_AUTOMATICALLY]}
                                </MenuItem>
                                <MenuItem value={STRIPE_COLLECTION_METHOD.SEND_INVOICE}>
                                    {COLLECTION_METHOD_LABELS[STRIPE_COLLECTION_METHOD.SEND_INVOICE]}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                </Grid>

                <TableContainer component={Paper} elevation={0}>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '22%' }}><b>Name / Email</b></TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '18%' }}><b>Insider ID</b></TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '12%' }}><b>Profile Type</b></TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '12%' }}><b>Current Plan</b></TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '10%' }}><b>Sub Status</b></TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '12%' }}><b>Payment Collection Method</b></TableCell>
                                <TableCell align="center" className={classes.tableHeaderCell} style={{ width: '220px' }}><b>Actions</b></TableCell>
                            </TableRow>
                        </TableHead>


                        <TableBody>
                            {profiles.map((profile) => (
                                <TableRow key={profile._id}>
                                    <TableCell>
                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                            {profile.first_name || profile.last_name 
                                                ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() 
                                                : profile.business_name || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {profile.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email) ? profile.email : ''}
                                        </Typography>

                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {profile.dq_insider_id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {getProfileTypeLabel(profile)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        {profile.dq_pro_plan ? (
                                            <Chip 
                                                label={DQ_PRO_PLAN_KEY_VERBOSE[profile.dq_pro_plan.plan_key] || profile.dq_pro_plan.plan_key} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined" 
                                            />
                                        ) : 'None'}
                                    </TableCell>

                                    <TableCell>
                                        <Chip 
                                            label={STRIPE_SUBSCRIPTION_STATUS_VERBOSE[profile.subscription_status] || profile.subscription_status || 'N/A'} 
                                            size="small"
                                            color={profile.subscription_status === 'active' ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {COLLECTION_METHOD_LABELS[profile.subscription_collection_method] || profile.subscription_collection_method || 'N/A'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Box display="flex" justifyContent="center" gridGap={8}>
                                            <Button 
                                                size="small" 
                                                color="primary" 
                                                variant="contained" 
                                                disableElevation
                                                className={classes.actionButton}
                                                onClick={() => handleViewDetails(profile.dq_insider_id)}
                                            >
                                                View PBDQ
                                            </Button>
                                            <Button 
                                                size="small" 
                                                color="secondary" 
                                                variant="contained" 
                                                disableElevation
                                                className={classes.actionButton}
                                                onClick={() => handleViewLedger(profile.dq_insider_id)}
                                            >
                                                View Credits Ledger
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disableElevation
                                                className={classes.actionButton}
                                                style={{ borderColor: '#0097b2', color: '#0097b2' }}
                                                onClick={() => handleViewDqtLedger(profile.dq_insider_id)}
                                            >
                                                View DQT Ledger
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {profiles.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">No insider profiles found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={filters.limit}
                    page={filters.page - 1}
                    onPageChange={(e, newPage) => handleFilterChange('page', newPage + 1)}
                    onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                />
                
                <LoaderWithBackDrop loading={loading} />
            </Paper>

            <PBDQDetailsDialog
                open={detailsOpen}
                onClose={handleDetailsClose}
                loading={detailsLoading}
                profile={selectedProfile}
                onActivated={fetchData}
                onRefresh={refreshSelectedProfile}
            />

            <DQInsiderLedgerDialog
                open={ledgerOpen}
                onClose={() => {
                    setLedgerOpen(false);
                    fetchData();
                }}
                dq_insider_id={selectedLedgerInsiderId}
            />

            <DQInsiderDqtLedgerDialog
                open={dqtLedgerOpen}
                onClose={() => setDqtLedgerOpen(false)}
                dq_insider_id={selectedDqtInsiderId}
            />
        </div>
    );
};

export default PBDQProfilesTab;
